if (!customElements.get('vanta-sidebar-menu')) {
  class VantaSidebarMenu extends HTMLElement {
    connectedCallback() {
      if (this.dataset.vantaSidebarReady === 'true') return;
      this.dataset.vantaSidebarReady = 'true';

      this.trigger = this.querySelector('[data-sidebar-trigger]');
      this.panel = this.querySelector('[data-sidebar-panel]');
      this.overlay = this.querySelector('[data-sidebar-overlay]');
      this.closeButton = this.querySelector('[data-sidebar-close]');
      this.desktopQuery = window.matchMedia('(min-width: 990px)');
      this.hoverTimer = null;
      this.previouslyFocused = null;

      if (!this.trigger || !this.panel || !this.overlay) return;

      this.onTriggerClick = this.onTriggerClick.bind(this);
      this.onOverlayClick = this.onOverlayClick.bind(this);
      this.onCloseClick = this.onCloseClick.bind(this);
      this.onDocumentKeydown = this.onDocumentKeydown.bind(this);
      this.onViewportChange = this.onViewportChange.bind(this);
      this.onPanelClick = this.onPanelClick.bind(this);
      this.updateOffset = this.updateOffset.bind(this);

      this.trigger.addEventListener('click', this.onTriggerClick);
      this.overlay.addEventListener('click', this.onOverlayClick);
      this.closeButton?.addEventListener('click', this.onCloseClick);
      this.panel.addEventListener('click', this.onPanelClick);
      document.addEventListener('keydown', this.onDocumentKeydown);
      window.addEventListener('resize', this.updateOffset, { passive: true });
      this.desktopQuery.addEventListener('change', this.onViewportChange);

      if (this.dataset.openOnHover === 'true') this.enableHoverControls();
    }

    disconnectedCallback() {
      this.trigger?.removeEventListener('click', this.onTriggerClick);
      this.overlay?.removeEventListener('click', this.onOverlayClick);
      this.closeButton?.removeEventListener('click', this.onCloseClick);
      this.panel?.removeEventListener('click', this.onPanelClick);
      document.removeEventListener('keydown', this.onDocumentKeydown);
      window.removeEventListener('resize', this.updateOffset);
      this.desktopQuery?.removeEventListener('change', this.onViewportChange);
      this.clearHoverTimer();
      this.unlockScroll();
    }

    enableHoverControls() {
      this.onTriggerEnter = () => {
        this.clearHoverTimer();
        this.open({ moveFocus: false });
      };

      this.onTriggerLeave = () => this.scheduleHoverClose();
      this.onPanelEnter = () => this.clearHoverTimer();
      this.onPanelLeave = () => this.scheduleHoverClose();

      this.trigger.addEventListener('mouseenter', this.onTriggerEnter);
      this.trigger.addEventListener('mouseleave', this.onTriggerLeave);
      this.panel.addEventListener('mouseenter', this.onPanelEnter);
      this.panel.addEventListener('mouseleave', this.onPanelLeave);
    }

    scheduleHoverClose() {
      this.clearHoverTimer();
      this.hoverTimer = window.setTimeout(() => {
        this.close({ restoreFocus: false });
      }, 220);
    }

    clearHoverTimer() {
      if (this.hoverTimer) window.clearTimeout(this.hoverTimer);
      this.hoverTimer = null;
    }

    onTriggerClick() {
      if (this.classList.contains('is-open')) {
        this.close();
      } else {
        this.open({ moveFocus: true });
      }
    }

    onOverlayClick() {
      this.close();
    }

    onCloseClick() {
      this.close();
    }

    onPanelClick(event) {
      if (event.target.closest('a[href]')) {
        this.close({ restoreFocus: false });
      }
    }

    onViewportChange(event) {
      if (!event.matches) this.close({ restoreFocus: false });
    }

    onDocumentKeydown(event) {
      if (!this.classList.contains('is-open')) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.key !== 'Tab') return;

      const focusable = this.getFocusableElements();
      if (!focusable.length) {
        event.preventDefault();
        this.panel.focus({ preventScroll: true });
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    open({ moveFocus = true } = {}) {
      if (!this.desktopQuery.matches || this.classList.contains('is-open')) return;

      this.previouslyFocused = document.activeElement;
      this.updateOffset();
      this.classList.add('is-open');
      this.trigger.setAttribute('aria-expanded', 'true');
      this.panel.setAttribute('aria-hidden', 'false');
      this.overlay.setAttribute('aria-hidden', 'false');

      if (this.dataset.lockScroll === 'true') {
        document.body.classList.add('vanta-sidebar-scroll-locked');
      }

      if (moveFocus) {
        window.requestAnimationFrame(() => {
          const focusTarget = this.closeButton || this.getFocusableElements()[0] || this.panel;
          focusTarget.focus({ preventScroll: true });
        });
      }
    }

    close({ restoreFocus = true } = {}) {
      if (!this.classList.contains('is-open')) return;

      this.classList.remove('is-open');
      this.trigger.setAttribute('aria-expanded', 'false');
      this.panel.setAttribute('aria-hidden', 'true');
      this.overlay.setAttribute('aria-hidden', 'true');
      this.unlockScroll();

      if (restoreFocus && this.previouslyFocused instanceof HTMLElement) {
        this.previouslyFocused.focus({ preventScroll: true });
      }
    }

    unlockScroll() {
      document.body.classList.remove('vanta-sidebar-scroll-locked');
    }

    updateOffset() {
      let offset = 0;

      if (this.dataset.belowHeader === 'true') {
        const headerSection = this.closest('.section-header');
        if (headerSection) {
          offset = Math.max(0, Math.round(headerSection.getBoundingClientRect().bottom));
        }
      }

      this.style.setProperty('--vanta-sidebar-offset', `${offset}px`);
    }

    getFocusableElements() {
      return Array.from(
        this.panel.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      ).filter((element) => !element.hasAttribute('hidden') && element.offsetParent !== null);
    }
  }

  customElements.define('vanta-sidebar-menu', VantaSidebarMenu);
}
